<template>
   <div class="insurance-overlay">
      <div class="insurance-modal">
         <div class="close-btn-wrapper">
            <i class="gts-icon icon-close-rounded icon-s close-btn" @click="close"></i>
         </div>

         <header class="modal-header">
            <h2>{{ $t("insuranceModal_lblInsuranceOffer") }}</h2>
            <p>
               {{ $t("insuranceModal_lblInsuranceInfoTxt") }}
            </p>
         </header>
         <div class="offers-container">
            <div v-for="(offer, index) in offers" :key="offer.identifier" :class="['offer-card', { selected: offer.selected }]">
               <div class="icon">
                  <img v-if="index == 0" src="@/assets/images/shield_unselected.png" />
                  <img v-else src="@/assets/images/shield_selected.png" />
               </div>

               <h3 class="alias">{{ offer.alias }}</h3>
               <p class="policy">{{ offer.policyDetails }}</p>

               <ul class="coverage-fees-list">
                  <li v-for="(fee, idx) in offer.coverageFees" :key="idx" class="coverage-fee-item">
                     <i class="gts-icon icon-sm" :class="fee.included ? 'icon-included' : 'icon-excluded'"></i>
                     <div class="fee-details">
                        <span class="fee-name">{{ fee.name }}</span>
                        <div v-if="index != 0" class="fee-bottom">
                           <span class="fee-price">{{ $t("insuranceModal_lblMax") }} : {{ $t(fee.price) }}</span>
                           <span class="fee-deductible">{{ $t("insuranceModal_lblDeductible") }} : {{ fee.deductible }}</span>
                        </div>
                     </div>
                  </li>
               </ul>

               <div class="price">
                  <span>+ {{ offer.total | price(offer.currency) }}</span>
               </div>

               <UIButton className="default" :text="offer.selected == false ? $t('insuranceModal_btnSelect') : $t('insuranceModal_btnUnSelect')" @click.native="selectOffer(offer)" />

               <a class="terms-link" :href="offer.termsAndConditionsLink" target="_blank">
                  {{ $t("insuranceModal_lblPolicyInformation") }}
               </a>
            </div>
         </div>

         <footer class="modal-footer">
            <button class="select-btn" v-if="isOfferSelected" @click="select">{{ $t("insuranceModal_btnContinueWithInsurance") }}</button>
            <button class="skip-btn" v-else @click="skip">{{ $t("insuranceModal_btnNoInsurance") }}</button>
         </footer>
      </div>
   </div>
</template>

<script>
import UIButton from "@/components/unitary/Button.vue";

export default {
   name: "InsuranceModal",
   components: {
      UIButton,
   },
   props: {
      offers: {
         type: Array,
         default: () => [],
      },
   },
   data() {
      return {
         isOfferSelected: false,
      };
   },
   methods: {
      selectOffer(offer) {
         if (!offer.selected) {
            this.offers.forEach((o) => {
               o.selected = false;
            });
            offer.selected = true;
            this.isOfferSelected = true;
         } else {
            offer.selected = false;
            this.isOfferSelected = false;
         }
      },
      close() {
         this.$emit("close");
      },
      skip() {
         this.$emit("skip");
      },
      select() {
         const offer = this.offers.find((offer) => offer.selected);
         this.$emit("selected", offer.identifier);
      },
      sortCoverageFees(coverageFees) {
         return coverageFees.sort((a, b) => b.included - a.included);
      },
   },
   mounted() {
      this.offers.forEach((offer) => {
         offer.coverageFees = this.sortCoverageFees(offer.coverageFees);
      });
   },
};
</script>

<style lang="scss" scoped>
.insurance-overlay {
   position: fixed;
   top: 0;
   left: 0;
   width: 100%;
   height: 100%;
   /* background-color: var(--gray-1000-alpha-75); */
   background-color: var(--background-overlay);
   display: flex;
   align-items: center;
   justify-content: center;
   z-index: 1000;
}

.insurance-modal {
   background: var(--gray-50);
   width: 700px;
   max-width: 90%;
   border-radius: 8px;
   overflow: hidden;
   display: flex;
   flex-direction: column;
}

.close-btn-wrapper {
   display: flex;
   justify-content: flex-end;
   position: relative;
   background: var(--gray-0);
   border-radius: 100%;
   width: 97%;
   z-index: 99;
   right: 0rem;
   top: 1rem;
   transform: translateY(-0.75rem) translateX(0.75rem);

   i {
      cursor: pointer;
   }
}

.modal-header {
   display: flex;
   align-items: flex-start;
   flex-direction: column;
   position: relative;
   padding: 24px;
   h2 {
      margin: 0;
      font-size: 22px;
      font-weight: 600;
      color: var(--gray-800);
   }
   p {
      margin: 8px 0 0;
      font-size: 14px;
      color: var(--gray-500);
   }
}

.offers-container {
   display: flex;
   padding: 24px;
   overflow: hidden;
   justify-content: space-between;
}

.offer-card {
   flex: 0 0 calc(50% - 10px);
   max-width: 50%;
   background: var(--gray-50);
   border: 2px solid var(--gray-200);
   border-radius: 8px;
   padding: 20px;
   /* box-shadow: 0 2px 6px var(--gray-1000-alpha-75); */
   box-shadow: var(--gts-box-shadow);
   display: flex;
   flex-direction: column;
   align-items: center;
   transition: border-color 0.2s, box-shadow 0.2s;

   &.selected {
      border-color: var(--blue-400);
      background-color: var(--cyan-0);
      /* box-shadow: 0 4px 12px var(--gray-1000-alpha-75); */
      box-shadow: var(--gts-box-shadow);
   }

   .icon {
      width: 168px;
      height: 168px;
      mix-blend-mode: multiply;
      margin-bottom: 16px;
   }

   .alias {
      margin: 0;
      font-size: 18px;
      font-weight: 500;
      color: var(--gray-900);
   }

   .policy {
      margin: 4px 0 16px;
      font-size: 14px;
      color: var(--gray-600);
   }

   .coverage-list {
      list-style: none;
      padding: 0;
      margin: 0 0 16px;
      width: 100%;
      li {
         font-size: 14px;
         color: var(--gray-700);
         display: flex;
         justify-content: space-between;
         margin-bottom: 6px;
         &:last-child {
            margin-bottom: 0;
         }
      }
   }

   .price {
      font-size: 24px;
      font-weight: 600;
      color: var(--gray-900);
      margin-bottom: 16px;
   }

   .terms-link {
      font-size: 12px;
      color: var(--blue-500);
      padding-top: 8px;
      &:hover {
         text-decoration: underline !important;
      }
   }
}

.modal-footer {
   padding: 16px 24px;
   text-align: right;
   .skip-btn,
   .select-btn {
      background: none;
      border: none;
      font-size: 14px;
      color: var(--gray-500);
      cursor: pointer;
      &:hover {
         text-decoration: underline;
      }
   }
}

.coverage-fees-list {
   list-style: none;
   padding: 0;
   margin: 0 0 16px 0;
   width: 100%;
   display: flex;
   flex-direction: column;
   gap: 10px;
   height: 220px;
   overflow-y: auto;

   &::-webkit-scrollbar {
      width: 8px;
   }
   &::-webkit-scrollbar-thumb {
      background-color: var(--gray-500);
      border-radius: 24rem;
      border-right: 2px solid var(--gray-50);
   }
   &::-webkit-scrollbar-track {
      background: var(--blue-200-alpha-25);
      border-radius: 24rem;
   }

   .coverage-fee-item {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      border-radius: 6px;
      padding: 0px 12px;

      .fee-icon {
         font-size: 22px;
         margin-top: 2px;
      }

      .fee-details {
         flex: 1;
         display: flex;
         flex-direction: column;

         .fee-name {
            font-weight: 500;
            font-size: 15px;
            color: var(--gray-900);
            margin-bottom: 4px;
         }

         .fee-bottom {
            display: flex;
            justify-content: space-between;
            font-size: 13px;
            color: var(--gray-600);

            .fee-price {
               font-weight: 400;
            }

            .fee-deductible {
               font-weight: 400;
            }
         }
      }
   }
}
</style>
